import type { ConflictResult, ConflictRule, ConflictRuleContext } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';

/**
 * Detects conflicts where a person is assigned to a shift that requires a skill
 * whose certification/license has expired by the scheduled date.
 *
 * For each date and employee, checks every assigned shift unit's required skills
 * against the employee's skill records. If a required skill has an `expiryDate`
 * that is before the scheduled date, a `skill_expired` conflict is produced.
 *
 * Requires `context.personSkills` and `context.shiftRequiredSkills` to be provided.
 * Returns empty results if context is missing.
 */
export class SkillExpiredConflictRule implements ConflictRule {
  readonly type = 'skill_expired' as const;

  detect(schedule: ScheduleMap, _events: unknown[], context?: ConflictRuleContext): ConflictResult[] {
    const personSkills = context?.personSkills;
    const shiftRequiredSkills = context?.shiftRequiredSkills;
    if (!personSkills || !shiftRequiredSkills) return [];

    const skillNames = context?.skillNames;
    const results: ConflictResult[] = [];

    for (const [date, daySchedule] of Object.entries(schedule)) {
      for (const [personId, unitIds] of Object.entries(daySchedule)) {
        const skills = personSkills.get(personId);
        if (!skills) continue;

        for (const unitId of unitIds) {
          this.checkSkillExpiry(results, date, personId, unitId, skills, shiftRequiredSkills, skillNames);
        }
      }
    }

    return results;
  }

  /**
   * Check if any required skill for a shift unit has expired by the given date.
   */
  private checkSkillExpiry(
    results: ConflictResult[],
    date: string,
    personId: string,
    unitId: string,
    personSkillList: Array<{ skillId: string; expiryDate: string | null }>,
    shiftRequiredSkills: Map<string, string[]>,
    skillNames?: Map<string, string>
  ): void {
    const requiredSkillIds = shiftRequiredSkills.get(unitId);
    if (!requiredSkillIds || requiredSkillIds.length === 0) return;

    for (const requiredSkillId of requiredSkillIds) {
      const personSkill = personSkillList.find(ps => ps.skillId === requiredSkillId);
      // Only flag expired skills, not missing skills (that's a different concern)
      if (!personSkill || !personSkill.expiryDate) continue;

      if (personSkill.expiryDate < date) {
        const skillName = skillNames?.get(requiredSkillId) ?? 'Expired skill';
        results.push({
          type: 'skill_expired',
          date,
          personId,
          eventLabel: skillName,
          calendarId: '',
          shiftUnitId: unitId,
        });
        // One conflict per (person, date, shiftUnit) is enough — no need to
        // report each expired skill separately for the same assignment
        return;
      }
    }
  }
}
